import { statusSentence } from "../lib/errorCopy";
import type { ElaborateApi } from "./client";
import {
  ApiError,
  type ActivityEvent,
  type ActivityQuery,
  type AdminUser,
  type Comment,
  type CommentBodyEdit,
  type CommentCreate,
  type CommentEditResponse,
  type CommentResponse,
  type CommentStatus,
  type DocumentDetail,
  type DocumentQuery,
  type DocumentSummary,
  type DocVersionDetail,
  type VersionRestoreReport,
  type DocVersionMeta,
  type ExportFormat,
  type ExportOptions,
  exportUrlFor,
  type Folder,
  type FolderMutationResponse,
  type GovernanceDetail,
  type GovernanceDoc,
  type GovernanceVersion,
  type Health,
  type InviteAcceptRequest,
  type InviteAcceptResponse,
  type InviteCreateRequest,
  type InviteCreateResponse,
  type InviteInfo,
  type LoginRequest,
  type LoginResponse,
  type MeResponse,
  type MeUpdate,
  type Note,
  type NoteCreate,
  type NoteSummary,
  type NoteUpdate,
  type NotificationFeed,
  type NotificationMark,
  type NotificationMarkAll,
  type PeopleInviteRequest,
  type PeopleInviteResponse,
  type PermissionMatrix,
  type PermissionToggle,
  type PersonMembership,
  type ProposalCreate,
  type ProposalResponse,
  type ProposalStatus,
  type ProposedRevision,
  type ReactionToggleResponse,
  type ReplyCreate,
  type ResetPasswordResponse,
  type RoleChangeRequest,
  type SearchResults,
  type StatusChange,
  type Ticket,
  type TicketQuery,
  type UploadDocumentRequest,
  type UploadDocumentResponse,
  type User,
  type VisibilityChange,
  type VisibilityChangeResponse,
  type WorkspaceCreateRequest,
  type WorkspaceDetail,
  type WorkspaceMember,
  type WorkspaceSettings,
  type WorkspaceSettingsUpdate,
  type WorkspaceSummary,
} from "./types";
import {
  type EditorToolbarConfig,
  parseToolbarConfigSafe,
} from "@norkron/web-elaborate-editor";
import type { CitationResolution } from "./citationTypes";
import type {
  ArticleUnitDeleteQueryWire,
  ArticleUnitPatchWire,
  ArticleUnitRowWire,
  DeletePlanWire,
  MergePartsWire,
  MergeResultWire,
} from "./articleAdminTypes";

/**
 * Real adapter — hits the elaborate backend at `/api` on the SAME origin the
 * SPA is served from (the Zig `elaborate-app` serves web/dist). Auth is the
 * session cookie, so every request sends `credentials: "include"`. Errors are
 * normalised to the structured ApiError envelope.
 */

const BASE = "/api";

interface FetchOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | undefined>;
}

/**
 * Read a non-HttpOnly cookie value by name. Used to echo the CSRF signed
 * double-submit token (P2-13): the backend sets a NON-HttpOnly `el_csrf` cookie
 * on login / GET /auth/me / GET /auth/csrf, and the SPA must echo it in the
 * `X-CSRF-Token` header on every state-changing write or the server rejects it
 * (ELABORATE_CSRF_ENFORCE defaults ON).
 */
function readCookie(name: string): string | undefined {
  const prefix = name + "=";
  for (const part of document.cookie.split(";")) {
    const c = part.trim();
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length));
  }
  return undefined;
}

/**
 * The app's ONE transport to `/api`. Exported (as `apiRequest`) so a surface that
 * is not part of `ElaborateApi` can still reach the backend through this exact
 * path — session cookie, CSRF echo, stale-token retry, `ApiError` normalisation —
 * instead of adding a fifth bespoke `fetch` wrapper. `api/privzBff.ts` is the
 * first such consumer.
 *
 * ⛔ Not a general escape hatch. A route that belongs to elaborate's own domain
 * belongs on `ElaborateApi` (where the mock adapter can double it); this is for
 * transports a LIBRARY owns, where the op→route mapping comes from that lib's
 * published manifest rather than from this file.
 */
async function request<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  let url = new URL(BASE + path, window.location.origin).toString();
  if (opts.query) {
    // Build the query string with encodeURIComponent (spaces → "%20"), NOT
    // URLSearchParams (spaces → "+"). The elaborate backend percent-decodes
    // query params but does NOT treat "+" as a space, so a folder name like
    // "EN Casino Reviews" must go over the wire as "EN%20Casino%20Reviews" or
    // the server-side `f.name = $folder` filter matches nothing and the folder
    // renders empty. (Root cause of the empty-folder acceptance blocker.)
    const qs = Object.entries(opts.query)
      .filter(([, v]) => v != null && v !== "")
      .map(
        ([k, v]) =>
          `${encodeURIComponent(k)}=${encodeURIComponent(v as string)}`,
      )
      .join("&");
    if (qs) url += (url.includes("?") ? "&" : "?") + qs;
  }

  const method = opts.method ?? "GET";
  const isWrite = method !== "GET" && method !== "HEAD";
  const buildHeaders = (csrf: string | undefined): Record<string, string> => {
    const h: Record<string, string> = {};
    if (opts.body) h["Content-Type"] = "application/json";
    // CSRF signed double-submit (P2-13): echo the `el_csrf` cookie in the
    // `X-CSRF-Token` header on every state-changing write. GET/HEAD are exempt, as
    // are the pre-session bootstrap routes server-side (login/logout/invite-accept/
    // csrf) — sending the header on those is harmless when the cookie is absent.
    if (isWrite && csrf) h["X-CSRF-Token"] = csrf;
    return h;
  };
  const send = (csrf: string | undefined): Promise<Response> => {
    const headers = buildHeaders(csrf);
    return fetch(url, {
      method,
      credentials: "include",
      headers: Object.keys(headers).length ? headers : undefined,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    });
  };

  let res: Response;
  try {
    const sent = readCookie("el_csrf");
    res = await send(sent);
    // Stale-CSRF recovery (ROOT CAUSE of the reported ONLINE "couldn't save"):
    // a long-lived tab whose signed CSRF token rotated server-side sends a stale
    // `X-CSRF-Token` and gets a hard 403 with no recovery. On a write 403, refresh
    // the token via `GET /api/auth/csrf` (re-issues the `el_csrf` cookie) and
    // retry ONCE — but only if the fresh token actually DIFFERS from the one we
    // sent, so a genuine authorization 403 (e.g. non-author edit) is surfaced
    // immediately, never masked or retried.
    if (isWrite && res.status === 403) {
      try {
        await fetch(new URL(BASE + "/auth/csrf", window.location.origin).toString(), {
          method: "GET",
          credentials: "include",
        });
        const fresh = readCookie("el_csrf");
        if (fresh && fresh !== sent) res = await send(fresh);
      } catch {
        /* keep the original 403 if the refresh itself failed */
      }
    }
  } catch (e) {
    throw new ApiError(0, "network_error", (e as Error).message);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const code =
      (data && typeof data === "object" && "code" in data
        ? String((data as { code: unknown }).code)
        : undefined) ?? `http_${res.status}`;
    const served =
      (data && typeof data === "object" && "message" in data
        ? String((data as { message: unknown }).message)
        : undefined) ?? res.statusText;
    // ⛔ NEVER an empty message. The edge serves HTTP/2, which has no reason
    // phrase, so `res.statusText` is the EMPTY STRING on every response — and
    // elaborate's own 404 body (`{"error":"not_found"}`) carries neither `code`
    // nor `message`. Every 404 therefore reached the UI as
    // `ApiError(404, "http_404", "")`, and every renderer printed its own
    // "An unexpected error occurred." — which is what a client actually saw on
    // the admin console. `statusSentence` is the floor: a sentence, never a code.
    const message = served.trim().length > 0 ? served : statusSentence(res.status);
    throw new ApiError(res.status, code, message);
  }

  return data as T;
}

export { request as apiRequest, type FetchOptions };

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function createRealAdapter(): ElaborateApi {
  return {
    login: (body: LoginRequest) =>
      request<LoginResponse>("/auth/login", { method: "POST", body }),
    logout: () =>
      request<void>("/auth/logout", { method: "POST" }).then(() => undefined),
    me: () => request<MeResponse>("/auth/me"),

    // export + version history (single-origin /api). exportUrl is a plain <a>
    // download href (the browser sends the session cookie same-origin). `opts`
    // appends the branded `template` + the `disposition` (inline preview).
    exportUrl: (docId: string, format: ExportFormat, opts?: ExportOptions) =>
      exportUrlFor(docId, format, opts),
    listVersions: (docId: string) =>
      request<DocVersionMeta[]>(
        `/documents/${encodeURIComponent(docId)}/versions`,
      ),
    getVersion: (docId: string, versionId: number) =>
      request<DocVersionDetail>(
        `/documents/${encodeURIComponent(docId)}/versions/${versionId}`,
      ),
    createVersion: (docId: string, note?: string) =>
      request<DocVersionMeta>(
        `/documents/${encodeURIComponent(docId)}/versions`,
        { method: "POST", body: { note: note ?? "" } },
      ),
    restoreVersion: (docId: string, versionId: number, note?: string) =>
      request<VersionRestoreReport>(
        `/documents/${encodeURIComponent(docId)}/versions/${versionId}/restore`,
        { method: "POST", body: { note: note ?? "" } },
      ),

    createInvite: (body: InviteCreateRequest) =>
      request<InviteCreateResponse>("/invites", { method: "POST", body }),
    getInvite: (token: string) =>
      request<InviteInfo>(`/invites/${encodeURIComponent(token)}`),
    acceptInvite: (token: string, body: InviteAcceptRequest) =>
      request<InviteAcceptResponse>(
        `/invites/${encodeURIComponent(token)}/accept`,
        { method: "POST", body },
      ),

    listWorkspaces: () => request<WorkspaceSummary[]>("/workspaces"),
    getWorkspace: (slug: string) =>
      request<WorkspaceDetail>(`/workspaces/${encodeURIComponent(slug)}`),
    createWorkspace: (body: WorkspaceCreateRequest) =>
      request<WorkspaceSummary>("/workspaces", { method: "POST", body }),

    createFolder: (slug: string, name: string): Promise<Folder> =>
      request<FolderMutationResponse>(
        `/workspaces/${encodeURIComponent(slug)}/folders`,
        { method: "POST", body: { name } },
      ).then((r) => r.folder),
    renameFolder: (id: string, name: string): Promise<Folder> =>
      request<FolderMutationResponse>(`/folders/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: { name },
      }).then((r) => r.folder),

    listDocuments: (slug: string, query?: DocumentQuery) =>
      request<DocumentSummary[]>(
        `/workspaces/${encodeURIComponent(slug)}/documents`,
        { query: { folder: query?.folder, status: query?.status } },
      ),
    getDocument: (id: string) =>
      request<DocumentDetail>(`/documents/${encodeURIComponent(id)}`),
    uploadDocument: (slug: string, body: UploadDocumentRequest) =>
      request<UploadDocumentResponse>(
        `/workspaces/${encodeURIComponent(slug)}/documents`,
        { method: "POST", body },
      ).then((r) => r.document),

    archiveDocument: (id: string) =>
      request<{ archived: boolean }>(`/documents/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }).then(() => undefined),
    restoreDocument: (id: string) =>
      request<{ restored: boolean }>(
        `/documents/${encodeURIComponent(id)}/restore`,
        { method: "POST" },
      ).then(() => undefined),
    listArchivedDocuments: (slug: string) =>
      request<DocumentSummary[]>(
        `/workspaces/${encodeURIComponent(slug)}/documents/archived`,
      ),

    search: async (slug: string, q: string) => {
      // Article-unit-first: the backend groups hits by unit (superseded history
      // excluded) and returns {units, other}. Ids arrive as JSON numbers; stringify
      // them so the drill links (`/article/:id`, `/doc/:id`) match the rest of the
      // client (mirrors articleUnits.mapSummary).
      const r = await request<SearchResults>(
        `/workspaces/${encodeURIComponent(slug)}/search`,
        { query: { q } },
      );
      return {
        units: r.units.map((u) => ({ ...u, id: String(u.id) })),
        other: r.other.map((o) => ({ ...o, id: String(o.id) })),
      };
    },

    listGovernance: (slug: string) =>
      request<GovernanceDoc[]>(
        `/workspaces/${encodeURIComponent(slug)}/governance`,
      ),
    getGovernanceDoc: (id: number) =>
      request<GovernanceDetail>(`/governance/documents/${id}`),
    saveGovernanceDoc: (id: number, body: string) =>
      request<{ version: GovernanceVersion }>(
        `/governance/documents/${id}`,
        { method: "PUT", body: { body } },
      ).then((r) => r.version),

    listActivity: (slug: string, query?: ActivityQuery) =>
      request<ActivityEvent[]>(
        `/workspaces/${encodeURIComponent(slug)}/activity`,
        {
          query: {
            before: query?.before != null ? String(query.before) : undefined,
            limit: query?.limit != null ? String(query.limit) : undefined,
            unit: query?.unit != null ? String(query.unit) : undefined,
            scope: query?.scope,
          },
        },
      ),

    listNotes: (slug: string, documentId?: string) =>
      request<NoteSummary[]>(
        `/workspaces/${encodeURIComponent(slug)}/notes`,
        { query: { document_id: documentId } },
      ),
    getNote: (id: string) => request<Note>(`/notes/${encodeURIComponent(id)}`),
    createNote: (slug: string, body: NoteCreate) =>
      request<Note>(`/workspaces/${encodeURIComponent(slug)}/notes`, {
        method: "POST",
        body,
      }),
    updateNote: (id: string, body: NoteUpdate) =>
      request<Note>(`/notes/${encodeURIComponent(id)}`, {
        method: "PUT",
        body,
      }),

    listTickets: (slug: string, query?: TicketQuery) =>
      request<Ticket[]>(`/workspaces/${encodeURIComponent(slug)}/tickets`, {
        query: {
          type: query?.type === "all" ? undefined : query?.type,
          status: query?.status === "all" ? undefined : query?.status,
          unit: query?.unit != null ? String(query.unit) : undefined,
        },
      }),

    listProposals: (slug: string, status?: ProposalStatus) =>
      request<ProposedRevision[]>(
        `/workspaces/${encodeURIComponent(slug)}/proposals`,
        { query: { status } },
      ),
    createProposal: (docId: string, body: ProposalCreate) =>
      request<ProposalResponse>(
        `/documents/${encodeURIComponent(docId)}/proposals`,
        { method: "POST", body },
      ).then((r) => r.proposal),
    acceptProposal: (id: number) =>
      request<ProposalResponse>(`/proposals/${id}/accept`, {
        method: "POST",
      }).then((r) => r.proposal),
    rejectProposal: (id: number) =>
      request<ProposalResponse>(`/proposals/${id}/reject`, {
        method: "POST",
      }).then((r) => r.proposal),

    getMe: () => request<{ user: User }>("/me").then((r) => r.user),
    updateMe: (body: MeUpdate) =>
      request<{ user: User }>("/me", { method: "PATCH", body }).then(
        (r) => r.user,
      ),
    getWorkspaceSettings: (slug: string) =>
      request<WorkspaceSettings>(
        `/workspaces/${encodeURIComponent(slug)}/settings`,
      ),
    updateWorkspaceSettings: (slug: string, body: WorkspaceSettingsUpdate) =>
      request<WorkspaceSettings>(
        `/workspaces/${encodeURIComponent(slug)}/settings`,
        { method: "PATCH", body },
      ),
    // The server returns the raw config JSON (the default when unset). Parse it
    // through the lib's safe parser so the caller ALWAYS gets a full, valid
    // EditorToolbarConfig (defaults filled), never a partial the renderer chokes on.
    getEditorToolbar: (slug: string) =>
      request<unknown>(
        `/workspaces/${encodeURIComponent(slug)}/settings/editor-toolbar`,
      ).then((raw) => parseToolbarConfigSafe(raw)),
    updateEditorToolbar: (slug: string, config: EditorToolbarConfig) =>
      request<unknown>(
        `/workspaces/${encodeURIComponent(slug)}/settings/editor-toolbar`,
        { method: "PATCH", body: config },
      ).then((raw) => parseToolbarConfigSafe(raw)),

    listComments: (docId: string) =>
      request<Comment[]>(`/documents/${encodeURIComponent(docId)}/comments`),
    createComment: (docId: string, body: CommentCreate) =>
      request<CommentResponse>(
        `/documents/${encodeURIComponent(docId)}/comments`,
        { method: "POST", body },
      ).then((r) => r.comment),
    replyToComment: (commentId: number, body: ReplyCreate) =>
      request<CommentResponse>(`/comments/${commentId}/replies`, {
        method: "POST",
        body,
      }).then((r) => r.comment),
    setCommentStatus: (commentId: number, status: CommentStatus) =>
      request<StatusChange>(`/comments/${commentId}/status`, {
        method: "PATCH",
        body: { status },
      }),
    // review-notes §3: forward-compatible with the backend `PATCH
    // /api/comments/:cid` route (design §3.1). Author-only + `updated_at` bump are
    // enforced server-side.
    editComment: (commentId: number, body: CommentBodyEdit) =>
      request<CommentEditResponse>(`/comments/${commentId}`, {
        method: "PATCH",
        body,
      }),
    // comment-delete: the author deletes their own note. Routed through
    // `request()` (NOT a bespoke fetch) so it inherits the signed double-submit
    // CSRF header AND the stale-CSRF single-retry — a long-lived tab whose token
    // rotated must not get a dead 403 on a destructive action. No body; 204 → void.
    deleteComment: (commentId: number) =>
      request<void>(`/comments/${commentId}`, { method: "DELETE" }),
    setCommentVisibility: (commentId: number, body: VisibilityChange) =>
      request<VisibilityChangeResponse>(`/comments/${commentId}/visibility`, {
        method: "PATCH",
        body,
      }),
    // review-notes §4: PUT adds / DELETE removes the caller's reaction (the emoji
    // is a path segment; `encodeURIComponent` matches the server's percent-decode).
    // No request body — the emoji rides the URL. Visibility-gated server-side.
    toggleReaction: (commentId: number, emoji: string, add: boolean) =>
      request<ReactionToggleResponse>(
        `/comments/${commentId}/reactions/${encodeURIComponent(emoji)}`,
        { method: add ? "PUT" : "DELETE" },
      ),
    listMembers: (docId: string) =>
      request<WorkspaceMember[]>(
        `/documents/${encodeURIComponent(docId)}/members`,
      ),

    listNotifications: () => request<NotificationFeed>("/notifications"),
    markNotificationRead: (id: number) =>
      request<NotificationMark>(`/notifications/${id}/read`, {
        method: "POST",
      }),
    markAllNotificationsRead: () =>
      request<NotificationMarkAll>("/notifications/read-all", {
        method: "POST",
      }),

    resolveCitation: (docId: string, commentId: number) =>
      request<CitationResolution>(
        `/documents/${encodeURIComponent(docId)}/anchor/${encodeURIComponent(
          String(commentId),
        )}`,
      ),

    // ── article-project lifecycle (rename / delete / merge) ───────────────
    // All four go through `request()` — the ONE transport with the stale-CSRF
    // refresh-and-retry-once recovery. `articleUnits.ts`'s local `postJson` has
    // no such recovery, and a destructive terminal that 403s irrecoverably in a
    // long-lived tab is not something to build a delete dialog on.
    patchArticleUnit: (unitId: string, body: ArticleUnitPatchWire) =>
      request<{ article_unit: ArticleUnitRowWire }>(
        `/article-units/${encodeURIComponent(unitId)}`,
        { method: "PATCH", body },
      ),
    // QUERY parameters, not a body — `DeleteQuery`'s FIELD names ARE these keys.
    // `request()` drops an undefined/empty value, so a `detach` delete puts NO
    // `confirm` on the wire at all (the server only compares it for `archive`).
    deleteArticleUnit: (unitId: string, query: ArticleUnitDeleteQueryWire) =>
      request<DeletePlanWire>(`/article-units/${encodeURIComponent(unitId)}`, {
        method: "DELETE",
        query: {
          documents: query.documents,
          dry_run: query.dry_run,
          confirm: query.confirm,
        },
      }),
    listArticleMergeCandidates: (unitId: string) =>
      request<{ candidates: ArticleUnitRowWire[] }>(
        `/article-units/${encodeURIComponent(unitId)}/parts`,
      ).then((r) => r?.candidates ?? []),
    mergeArticleParts: (unitId: string, body: MergePartsWire) =>
      request<MergeResultWire>(
        `/article-units/${encodeURIComponent(unitId)}/parts`,
        { method: "POST", body },
      ),

    listPeople: (slug: string, q?: string) =>
      request<PersonMembership[]>(
        `/workspaces/${encodeURIComponent(slug)}/people`,
        { query: { q } },
      ),
    invitePerson: (slug: string, body: PeopleInviteRequest) =>
      request<PeopleInviteResponse>(
        `/workspaces/${encodeURIComponent(slug)}/people`,
        { method: "POST", body },
      ),
    changeMemberRole: (slug: string, userId: string, body: RoleChangeRequest) =>
      request<PersonMembership>(
        `/workspaces/${encodeURIComponent(slug)}/people/${encodeURIComponent(userId)}`,
        { method: "PATCH", body },
      ),
    removeMember: (slug: string, userId: string) =>
      request<void>(
        `/workspaces/${encodeURIComponent(slug)}/people/${encodeURIComponent(userId)}`,
        { method: "DELETE" },
      ).then(() => undefined),

    // ── admin (staff / ops plane) — `/api/admin/*` served by elaborate-ops ──────
    // In the deployed topology a Tailscale-scoped reverse proxy routes
    // `/api/admin/*` to the ops plane; every route is additionally is_staff-gated
    // server-side (the client guard is convenience only).
    listUsers: (q?: string) =>
      request<{ users: AdminUser[] }>("/admin/users", { query: { q } }).then(
        (r) => r.users,
      ),
    setUserStaff: (userId: string, isStaff: boolean) =>
      request<AdminUser>(`/admin/users/${encodeURIComponent(userId)}/staff`, {
        method: "PATCH",
        body: { is_staff: isStaff },
      }),
    setUserStatus: (userId: string, status: string) =>
      request<AdminUser>(`/admin/users/${encodeURIComponent(userId)}/status`, {
        method: "PATCH",
        body: { status },
      }),
    renameUser: (userId: string, name: string) =>
      request<AdminUser>(`/admin/users/${encodeURIComponent(userId)}/name`, {
        method: "PATCH",
        body: { name },
      }),
    resetUserPassword: (userId: string) =>
      request<ResetPasswordResponse>(
        `/admin/users/${encodeURIComponent(userId)}/reset-password`,
        { method: "POST" },
      ),
    getPermissionMatrix: (slug: string) =>
      request<PermissionMatrix>(
        `/admin/workspaces/${encodeURIComponent(slug)}/permissions`,
      ),
    setPermission: (slug: string, body: PermissionToggle) =>
      request<PermissionToggle>(
        `/admin/workspaces/${encodeURIComponent(slug)}/permissions`,
        { method: "PATCH", body },
      ),
    resetRolePermissions: (slug: string, role: string) =>
      request<{ role: string; reset: boolean }>(
        `/admin/workspaces/${encodeURIComponent(slug)}/permissions/reset`,
        { method: "POST", body: { role } },
      ).then(() => undefined),

    health: () => request<Health>("/health"),
  };
}
